import { formatMoney, formatRate } from '@/utils/format';
import type { Kpi, KpiUnit } from '@/types/insight';

/*
 * Formatting for dashboard figures. The unit says how to format a number,
 * never how to compute it: every value is shown as the server sent it.
 */

const COUNT_FORMAT = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });

/** One figure in the given unit. Also usable for trend points and chart axes. */
export function formatUnitValue(
  unit: KpiUnit,
  value: number | null | undefined,
  currencyCode?: string,
): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '—';
  }
  switch (unit) {
    case 'MONEY':
      return formatMoney(value, currencyCode);
    case 'PERCENT':
      return formatRate(value);
    case 'COUNT':
    default:
      return COUNT_FORMAT.format(value);
  }
}

/** A KPI's headline value, formatted by its unit. */
export function formatKpiValue(kpi: Kpi, currencyCode?: string): string {
  return formatUnitValue(kpi.unit, kpi.value, currencyCode);
}

export type KpiDeltaDirection = 'up' | 'down' | 'flat';

export interface KpiDelta {
  /** value − previous, rounded to two places for display. */
  delta: number;
  direction: KpiDeltaDirection;
  /**
   * The signed change, formatted by unit: "+12", "−PKR 1,200.00", "+2.5 pts"
   * (percentage KPIs change by points, not percent).
   */
  text: string;
  /** The server's own words for the comparison period, e.g. "last month"; may be null. */
  comparison: string | null;
}

/**
 * The change between a KPI's value and its server-provided `previous`.
 *
 * This subtraction is the only arithmetic the dashboard performs. Returns null
 * when `previous` is null — there is no honest comparison, so none is shown.
 */
export function formatKpiDelta(kpi: Kpi, currencyCode?: string): KpiDelta | null {
  if (kpi.previous === null || kpi.previous === undefined) {
    return null;
  }
  // Rounded to cents so floating-point noise (0.30000000000000004) never shows.
  const delta = Math.round((kpi.value - kpi.previous) * 100) / 100;
  const direction: KpiDeltaDirection = delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat';
  const sign = delta > 0 ? '+' : delta < 0 ? '−' : '';
  const magnitude = Math.abs(delta);

  let text: string;
  switch (kpi.unit) {
    case 'MONEY':
      text = `${sign}${formatMoney(magnitude, currencyCode)}`;
      break;
    case 'PERCENT':
      text = `${sign}${magnitude.toFixed(1)} pts`;
      break;
    case 'COUNT':
    default:
      text = `${sign}${COUNT_FORMAT.format(magnitude)}`;
  }

  return { delta, direction, text, comparison: kpi.comparison ?? null };
}
